import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import {
  cardClass,
  borderGrey,
  primaryText,
  secondaryText,
  tertiaryText,
  semiBoldText,
} from '../../../../styling/stylingHelper';
import type { ConversationUsage, UsageStat } from '../../../../services/admin/cloudAgentTypes';

/** Compact token count: 940 → "940", 12 400 → "12.4k". */
function formatTokens(total: number): string {
  if (total < 1000) return String(total);
  return `${(total / 1000).toFixed(1)}k`;
}

/** Every token a stat covers — input + output + both cache legs. */
function totalTokens(stat: UsageStat): number {
  return stat.inputTokens + stat.outputTokens + stat.cacheReadTokens + stat.cacheWriteTokens;
}

/** USD, 4 decimals — the per-turn amounts are fractions of a cent. */
const usd = (value: number) => `$${value.toFixed(4)}`;

interface CostWidgetProps {
  /** Per-turn rows, in turn order. */
  turns: UsageStat[];
  /** The server's running conversation total (null before the first `usage` frame). */
  conversation: ConversationUsage | null;
  /** True while a turn is in flight — the pill shows "…" for the not-yet-counted turn. */
  isStreaming: boolean;
}

/**
 * Conversation cost pill + breakdown popover (super-admin diagnostic). Values are USD and come
 * straight from the server's `usage` frame — the frontend never prices tokens itself.
 */
export const CostWidget: React.FC<CostWidgetProps> = ({ turns, conversation, isStreaming }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const pillText = conversation
    ? `${usd(conversation.costUsd)} · ${formatTokens(totalTokens(conversation))} ${t('cloudAgent.cost.tokensShort')}`
    : t('cloudAgent.cost.empty');

  return (
    <div className="relative max-w-full">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition tabular-nums',
          'max-w-full whitespace-nowrap',
          borderGrey,
          'hover:bg-purple-50 dark:hover:bg-purple-900/20',
          secondaryText
        )}
        title={t('cloudAgent.cost.title')}
      >
        {pillText}
        {isStreaming && <span className={tertiaryText}>…</span>}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className={cn(
              cardClass,
              'absolute right-0 mt-2 z-20 w-[min(92vw,26rem)] p-3 shadow-lg border',
              borderGrey
            )}
          >
            <p className={cn(primaryText, 'text-sm', semiBoldText)}>{t('cloudAgent.cost.title')}</p>

            {turns.length === 0 ? (
              <p className={cn(secondaryText, 'text-xs mt-2')}>{t('cloudAgent.cost.noTurns')}</p>
            ) : (
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-[11px] tabular-nums">
                  <thead>
                    <tr className={cn(tertiaryText, 'text-left')}>
                      <th className="py-1 pr-2 font-medium">{t('cloudAgent.cost.turn')}</th>
                      <th className="py-1 pr-2 font-medium">{t('cloudAgent.cost.model')}</th>
                      <th className="py-1 pr-2 font-medium text-right">{t('cloudAgent.cost.in')}</th>
                      <th className="py-1 pr-2 font-medium text-right">{t('cloudAgent.cost.out')}</th>
                      <th className="py-1 pr-2 font-medium text-right">{t('cloudAgent.cost.cacheRead')}</th>
                      <th className="py-1 pr-2 font-medium text-right">{t('cloudAgent.cost.cacheWrite')}</th>
                      <th className="py-1 font-medium text-right">{t('cloudAgent.cost.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {turns.map((turn, i) => (
                      <tr key={i} className={cn('border-t', borderGrey)}>
                        <td className={cn(secondaryText, 'py-1 pr-2')}>{i + 1}</td>
                        <td className={cn(secondaryText, 'py-1 pr-2 whitespace-nowrap')}>{turn.model}</td>
                        <td className={cn(secondaryText, 'py-1 pr-2 text-right')}>{formatTokens(turn.inputTokens)}</td>
                        <td className={cn(secondaryText, 'py-1 pr-2 text-right')}>{formatTokens(turn.outputTokens)}</td>
                        <td className={cn(secondaryText, 'py-1 pr-2 text-right')}>{formatTokens(turn.cacheReadTokens)}</td>
                        <td className={cn(secondaryText, 'py-1 pr-2 text-right')}>{formatTokens(turn.cacheWriteTokens)}</td>
                        <td className={cn(primaryText, 'py-1 text-right')}>{usd(turn.costUsd)}</td>
                      </tr>
                    ))}
                    {conversation && (
                      <tr className={cn('border-t', borderGrey, semiBoldText)}>
                        <td className={cn(primaryText, 'py-1.5 pr-2')} colSpan={2}>
                          {t('cloudAgent.cost.total', { n: conversation.turnCount })}
                        </td>
                        <td className={cn(primaryText, 'py-1.5 pr-2 text-right')}>{formatTokens(conversation.inputTokens)}</td>
                        <td className={cn(primaryText, 'py-1.5 pr-2 text-right')}>{formatTokens(conversation.outputTokens)}</td>
                        <td className={cn(primaryText, 'py-1.5 pr-2 text-right')}>{formatTokens(conversation.cacheReadTokens)}</td>
                        <td className={cn(primaryText, 'py-1.5 pr-2 text-right')}>{formatTokens(conversation.cacheWriteTokens)}</td>
                        <td className={cn(primaryText, 'py-1.5 text-right')}>{usd(conversation.costUsd)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CostWidget;
